import { inject, injectable } from 'inversify'
import { interval, Subscription, switchMap } from 'rxjs'

import { InjectionTokens } from '@/controller/tokens'

import { SessionExpired } from '@/domain/events/SessionExpired'
import { Result } from '@/domain/protocols/Result'
import { AuthState } from '@/domain/states/AuthState'

import { IService } from '../IService'

import { I18n } from '@/i18n/I18n'
import { ACCOUNT_ROUTES, redirectToAccount } from '@/infra/Account/accountUrl'
import type { IAccountWebClient } from '@/infra/Account/IAccountWebClient'
import { readAuthCookie, removeAuthCookie } from '@/infra/http/authCookie'
import type { ILogger } from '@/infra/logger/ILogger'
import { ModalUtil } from '@/utils/modalUtil'

/** Capability do Account que libera o uso do PlanningApp. */
export const PLANNINGAPP_CAPABILITY = 'product:planningapp'

/** Intervalo da checagem periódica da capability com o app aberto. */
export const SUBSCRIPTION_CHECK_INTERVAL_MS = 1000 * 60 * 5 // 5 minutos

/** Ids dos modais que bloqueiam o app, para fechá-los pelo `ModalUtil.global.hide`. */
export const SUBSCRIPTION_BLOCKED_MODAL_ID = 'authenticator-subscription-blocked'
export const SESSION_EXPIRED_MODAL_ID = 'authenticator-session-expired'

@injectable()
export class Authenticator implements IService {
  private _subscriptionChecker!: Subscription
  private _sessionExpiredSubscription: Subscription | null = null
  private _isSubscriptionBlocked = false
  /**
   * Token da sessão que expirou, enquanto o modal de sessão expirada está aberto (`undefined` quando não
   * está). Serve para o botão do modal saber se o cookie tem uma sessão nova, feita em outra aba.
   */
  private _expiredSessionToken: string | null | undefined = undefined
  private _syncTokenFromCookie = () => {
    // Com a sessão expirada o app fica bloqueado até o usuário sair pelo modal: um token novo no cookie
    // pode ser de outra pessoa, e só o boot carrega os dados do usuário certo.
    if (this._isSessionExpired) return

    const cookieToken = readAuthCookie()
    if (!cookieToken) {
      // O `_nat` sumiu com a aba aberta (logout pelo Account ou por outra aba): não há mais sessão.
      this._handleSessionExpired()
      return
    }
    if (cookieToken !== this.authState.getStateSnapshot().token) {
      this.logger.log('Authenticator', 'Token do cookie mudou; atualizando a sessão em memória')
      this.authState.setToken(cookieToken)
    }
  }
  constructor(
    @inject(InjectionTokens.AuthState) private authState: AuthState,
    @inject(InjectionTokens.AccountWebClient) private accountWebClient: IAccountWebClient,
    @inject(InjectionTokens.I18n) private i18n: I18n,
    @inject(InjectionTokens.Logger) private logger: ILogger
  ) {}

  async init(): Promise<Result<void>> {
    const authToken = this._getAuthenticationCookie()
    if (!authToken) {
      return Result.fail('AUTHENTICATION_COOKIE_NOT_FOUND')
    } else {
      this.authState.setToken(authToken)
    }

    return Result.ok()
  }
  async start(): Promise<Result<void>> {
    if (!this._checkSubscriptionStatus()) {
      return Result.fail('SUBSCRIPTION_ERROR')
    }

    // O token só era lido no boot. Como o cookie `_nat` é de domínio compartilhado com o Account e os
    // outros apps da NexAtlas, um login/logout feito em outra aba o rotaciona enquanto esta aba está
    // aberta — releitura ao voltar para a aba resolve o caso comum antes de qualquer requisição falhar.
    window.addEventListener('focus', this._syncTokenFromCookie)
    document.addEventListener('visibilitychange', this._syncTokenFromCookie)

    // O AxiosHttpClient já tenta se recuperar relendo o cookie; quando não há token novo, a sessão
    // acabou de verdade e o usuário precisa saber — antes disso ele só via "erro ao salvar".
    this._sessionExpiredSubscription = SessionExpired.subscribe(() => this._handleSessionExpired())

    this._subscriptionChecker = interval(SUBSCRIPTION_CHECK_INTERVAL_MS)
      .pipe(switchMap(() => this._checkAccess()))
      .subscribe()

    return Result.ok()
  }

  stop(): Result<void> {
    window.removeEventListener('focus', this._syncTokenFromCookie)
    document.removeEventListener('visibilitychange', this._syncTokenFromCookie)

    if (this._sessionExpiredSubscription) {
      this._sessionExpiredSubscription.unsubscribe()
      this._sessionExpiredSubscription = null
    }

    if (this._subscriptionChecker) {
      this._subscriptionChecker.unsubscribe()
      return Result.ok()
    } else {
      return Result.fail('SubscriptionChecker not found')
    }
  }

  private get _isSessionExpired(): boolean {
    return this._expiredSessionToken !== undefined
  }

  /**
   * Sem sessão o app fica bloqueado: o modal não fecha e o botão leva ao login do Account, que devolve o
   * usuário para a URL atual.
   */
  private _handleSessionExpired() {
    if (this._isSessionExpired) return
    this._expiredSessionToken = this.authState.getStateSnapshot().token

    this.logger.warn('Authenticator', 'Sessão expirada: a API rejeitou o token ou o cookie _nat sumiu')

    ModalUtil.global.show({
      id: SESSION_EXPIRED_MODAL_ID,
      isBlocking: true,
      title: this.i18n.resolveToken('MODAL_SESSION-EXPIRED_TITLE'),
      headerDescription: this.i18n.resolveToken('MODAL_SESSION-EXPIRED_DESCRIPTION'),
      positiveLabel: this.i18n.resolveToken('MODAL_SESSION-EXPIRED_BUTTON'),
      onPositive: () => {
        this._leaveExpiredSession()
        return false
      }
    })
  }

  private _leaveExpiredSession() {
    const cookieToken = readAuthCookie()
    if (cookieToken && cookieToken !== this._expiredSessionToken) {
      // O usuário já entrou de novo em outra aba: o boot lê a sessão nova, sem passar pelo login.
      window.location.reload()
      return
    }

    // Sem apagar o cookie, a volta do login reaproveitaria o mesmo token recusado.
    removeAuthCookie()
    redirectToAccount(ACCOUNT_ROUTES.login)
  }

  private _getAuthenticationCookie() {
    return readAuthCookie()
  }

  /**
   * Roda a cada 5 minutos. Sem o cookie `_nat`, bloqueia pela sessão. Com ele, refaz o
   * `GET /v2/users/me` (que atualiza `user` e `capabilities` no `AuthState`), porque `capabilities` não
   * tem data para comparar, e bloqueia ou desbloqueia conforme a capability. Se a requisição falhar,
   * nada muda: o usuário não é bloqueado por rede ou indisponibilidade do Account, e quem já estava
   * bloqueado continua. Um token recusado não passa por aqui — o `AxiosHttpClient` emite `SessionExpired`.
   */
  private async _checkAccess(): Promise<void> {
    if (this._isSessionExpired) return

    if (!readAuthCookie()) {
      this._handleSessionExpired()
      return
    }

    const result = await this.accountWebClient.fetchAccount()
    if (result.isFailure) {
      this.logger.warn('Authenticator', `Checagem periódica da assinatura sem resposta do Account (${result.error})`)
      return
    }

    if (this._checkSubscriptionStatus()) {
      this._unblockSubscription()
    } else {
      this._blockSubscription()
    }
  }

  /** Sem a capability, o app fica bloqueado: o modal não fecha e o botão leva à assinatura do Account. */
  private _blockSubscription() {
    if (this._isSubscriptionBlocked) return
    this._isSubscriptionBlocked = true

    this.logger.warn('Authenticator', `Usuário sem a capability ${PLANNINGAPP_CAPABILITY}; bloqueando o app`)

    ModalUtil.global.show({
      id: SUBSCRIPTION_BLOCKED_MODAL_ID,
      isBlocking: true,
      title: this.i18n.resolveToken('MODAL_SUBSCRIPTION_ACCOUNT-PROBLEM_TITLE'),
      headerDescription: this.i18n.resolveToken('MODAL_SUBSCRIPTION_ACCOUNT-PROBLEM_DESCRIPTION'),
      positiveLabel: this.i18n.resolveToken('MODAL_SUBSCRIPTION_ACCOUNT-PROBLEM_BUTTON'),
      onPositive: () => {
        redirectToAccount(ACCOUNT_ROUTES.subscription)
        return false
      }
    })
  }

  /** A capability voltou com o app aberto (ex.: o usuário assinou em outra aba): libera o uso. */
  private _unblockSubscription() {
    if (!this._isSubscriptionBlocked) return
    this._isSubscriptionBlocked = false

    this.logger.log('Authenticator', `Capability ${PLANNINGAPP_CAPABILITY} de volta; desbloqueando o app`)
    ModalUtil.global.hide(SUBSCRIPTION_BLOCKED_MODAL_ID)
  }

  /**
   * O acesso ao app é liberado pela capability `product:planningapp`, que vem do Account em
   * `GET /v2/users/me`. No boot, as capabilities já foram gravadas pelo `AccountWebClient.init()`.
   */
  private _checkSubscriptionStatus(): boolean {
    const { capabilities } = this.authState.getStateSnapshot()
    return Array.isArray(capabilities) && capabilities.includes(PLANNINGAPP_CAPABILITY)
  }
}
